import {useMemo} from 'react'
import {ContentArea} from '@/components/shared/ContentArea'
import {InlineRow} from '@/components/shared/InlineRow'
import {Strong} from '@/components/shared/Strong'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Link} from 'react-router-dom'
import {AlertTriangle, BarChart3, CheckCircle, Cog, PlayCircle, Timer} from 'lucide-react'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {SegmentedProgress, type SegmentedProgressTone} from '@/components/ui/progress'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {ClickableCard} from '@/components/shared/ClickableCard'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {PageLayout} from '@/components/shared/PageLayout'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {JobSource} from '@/types/background-jobs'
import {useJobSourceLabel} from '@/features/admin/labels'
import {formatDuration, formatTimestamp} from '@/lib/date-utils'
import {CONCEPT_ICON} from '@/lib/concepts'

export function BackgroundJobsDashboard() {
    const i18n = useI18n()
    const jobSourceLabel = useJobSourceLabel()
    const services = useServices()
    const queryClient = useQueryClient()

    // Fetch stats (24h default)
    const {data: stats, isLoading: statsLoading, error: statsError} = useQuery({
        queryKey: ['job-stats'],
         
        queryFn: () => services.jobs.stats(),
        refetchInterval: 10000,
    })

    // Fetch recent executions (last 10)
    const {data: recentExecs} = useQuery({
        queryKey: ['recent-executions'],
         
        queryFn: () => services.jobs.executions({size: 10, sort: 'startedAt,desc'}),
        refetchInterval: 10000,
    })

    // Fetch all jobs for source breakdown
    const {data: jobsData} = useQuery({
        queryKey: ['jobs-source-breakdown'],
        queryFn: () => services.jobs.list({size: 200}),
    })

    // Compute source breakdown
    const sourceBreakdown = useMemo(() => {
        if (!jobsData?.content) return []
        const counts = new Map<JobSource, number>()
        for (const job of jobsData.content) {
            if (!job.source) continue
            counts.set(job.source, (counts.get(job.source) ?? 0) + 1)
        }
        const total = jobsData.content.length
        return Array.from(counts.entries()).map(([source, count]) => ({
            source,
            count,
            percent: total > 0 ? Math.round((count / total) * 100) : 0,
        }))
    }, [jobsData])

    // Compute needs attention items
    const needsAttention = useMemo(() => {
        const items: Array<{label: string, href: string, severity: number}> = []
        if (stats) {
            if (stats.failed > 0) {
                items.push({
                    label: i18n.backgroundJobs.failed as string + `: ${stats.failed}`,
                    href: routes.backgroundJobs.executions.list('status=FAILED'),
                    severity: 0,
                })
            }
            if (stats.running > 0) {
                items.push({
                    label: i18n.backgroundJobs.running as string + `: ${stats.running}`,
                    href: routes.backgroundJobs.executions.list('status=RUNNING'),
                    severity: 2,
                })
            }
            const disabledCount = (stats.totalJobs || 0) - (stats.enabledJobs || 0)
            if (disabledCount > 0) {
                items.push({
                    label: i18n.backgroundJobs.disabled as string + `: ${disabledCount}`,
                    href: routes.backgroundJobs.jobs.list(),
                    severity: 3,
                })
            }
        }
        return items.sort((a, b) => a.severity - b.severity)
    }, [stats, i18n])

    const handleRetry = () => {
        queryClient.invalidateQueries({queryKey: ['job-stats']})
        queryClient.invalidateQueries({queryKey: ['recent-executions']})
        queryClient.invalidateQueries({queryKey: ['jobs-source-breakdown']})
    }

    const recentExecutions = recentExecs?.content || []

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.dashboard}
            title={i18n.backgroundJobs.dashboardPageTitle}
            loading={statsLoading}
            error={statsError}
            onRetry={handleRetry}
            data-testid="background-jobs-dashboard-page"
        >
            {/* Top Row: 4 Stat Cards */}
            <DetailGrid columns={4}>
                {/* Total Jobs */}
                <Card data-testid="stat-total-jobs">
                    <CardContent className="pt-6">
                        <InlineRow justify="between">
                            <div>
                                <MutedText>{i18n.backgroundJobs.totalJobs}</MutedText>
                                <StatValue value={stats?.totalJobs || 0}/>
                                <MutedText size="xs" className="mt-1">
                                    {i18n.backgroundJobs.enabledOfTotal({enabled: stats?.enabledJobs || 0, total: stats?.totalJobs || 0})}
                                </MutedText>
                            </div>
                            <Icon icon={Cog} size="xl" muted/>
                        </InlineRow>
                    </CardContent>
                </Card>

                {/* Running Now */}
                {/* eslint-disable-next-line i18next/no-literal-string -- URL query param */}
                <ClickableCard to={routes.backgroundJobs.executions.list('status=RUNNING')} data-testid="stat-running-now">
                    <CardContent className="pt-6">
                        <InlineRow justify="between">
                            <div>
                                <MutedText>{i18n.backgroundJobs.runningNow}</MutedText>
                                <StatValue value={stats?.running || 0}/>
                            </div>
                            <Icon icon={PlayCircle} size="xl" className="text-status-info"/>
                        </InlineRow>
                    </CardContent>
                </ClickableCard>

                {/* Failed 24h */}
                <Card data-testid="stat-failed-24h">
                    <CardContent className="pt-6">
                        <InlineRow justify="between">
                            <div>
                                <MutedText>{i18n.backgroundJobs.failed24h}</MutedText>
                                <StatValue value={stats?.failed || 0} variant={(stats?.failed || 0) > 0 ? 'danger' : 'default'}/>
                            </div>
                            <Icon icon={AlertTriangle} size="xl" className={(stats?.failed || 0) > 0 ? 'text-destructive' : 'text-muted-foreground'}/>
                        </InlineRow>
                    </CardContent>
                </Card>

                {/* Avg Duration */}
                <Card data-testid="stat-avg-duration">
                    <CardContent className="pt-6">
                        <InlineRow justify="between">
                            <div>
                                <MutedText>{i18n.backgroundJobs.avgDuration}</MutedText>
                                <StatValue value={formatDuration(stats?.avgDurationMs || 0)}/>
                            </div>
                            <Icon icon={Timer} size="xl" muted/>
                        </InlineRow>
                    </CardContent>
                </Card>
            </DetailGrid>

            {/* Middle Row: Needs Attention + Recent Executions */}
            <DetailGrid columns={2}>
                {/* Needs Attention */}
                <Card data-testid="needs-attention">
                    <CardHeader>
                        <CardTitle className="text-lg" data-testid="card-title-needs-attention">{i18n.backgroundJobs.needsAttention}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {needsAttention.length === 0 ? (
                            <InlineRow gap="lg" className="py-4 text-sm" data-testid="all-healthy">
                                <Icon icon={CheckCircle} size="lg" className="text-status-success"/>
                                <MutedText>{i18n.backgroundJobs.allSystemsHealthy}</MutedText>
                            </InlineRow>
                        ) : (
                            <Stack gap="sm">
                                {needsAttention.map((item, idx) => (
                                    <Link
                                        key={idx}
                                        to={item.href}
                                        data-testid={`attention-item-${idx}`}
                                    >
                                        <InlineRow surface="hoverableLink" gap="md">
                                            <Icon icon={AlertTriangle} size="md" className="text-status-warning shrink-0"/>
                                            <span>{item.label}</span>
                                        </InlineRow>
                                    </Link>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Executions */}
                <Card data-testid="recent-executions">
                    <CardHeader>
                        <InlineRow justify="between">
                            <CardTitle className="text-lg" data-testid="card-title-recent-executions">{i18n.backgroundJobs.recentExecutions}</CardTitle>
                            <Link
                                to={routes.backgroundJobs.executions.list()}
                                className="text-primary hover:underline"
                                data-testid="link-view-all-executions"
                            >
                                {i18n.backgroundJobs.viewAll}
                            </Link>
                        </InlineRow>
                    </CardHeader>
                    <CardContent>
                        {recentExecutions.length === 0 ? (
                            <MutedText className="block py-4">{i18n.jobs.noExecutionsYet}</MutedText>
                        ) : (
                            <Stack gap="sm">
                                {recentExecutions.map(exec => (
                                    <Link
                                        key={exec.id}
                                        to={routes.backgroundJobs.executions.detail(exec.id)}
                                        data-testid={`recent-exec-${exec.id}`}
                                    >
                                        <InlineRow surface="hoverableLink" justify="between">
                                            <InlineRow gap="md" className="min-w-0">
                                                <StatusBadge status={exec.status} type={StatusBadgeType.EXECUTION_STATUS} compact/>
                                                <Strong truncate>{exec.jobName}</Strong>
                                            </InlineRow>
                                            <InlineRow gap="lg" className="shrink-0">
                                                <MutedText>{formatTimestamp(exec.startedAt)}</MutedText>
                                                {exec.durationMs != null && <MutedText>{formatDuration(exec.durationMs)}</MutedText>}
                                            </InlineRow>
                                        </InlineRow>
                                    </Link>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </DetailGrid>

            {/* Bottom: Source Breakdown */}
            <Card data-testid="source-breakdown">
                <CardHeader>
                    <CardTitle className="text-lg" data-testid="card-title-source-breakdown">
                        <IconText icon={BarChart3} size="md">{i18n.backgroundJobs.sourceBreakdown}</IconText>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {sourceBreakdown.length === 0 ? (
                        <MutedText>{i18n.jobs.noJobsFound}</MutedText>
                    ) : (
                        <Stack gap="sm">
                            {sourceBreakdown.map(({source, count, percent}) => {
                                const tone: SegmentedProgressTone = source === JobSource.STATIC || source === JobSource.DYNAMIC
                                    ? 'brand'
                                    : 'neutral'
                                return (
                                    <InlineRow key={source} gap="lg">
                                        <Badge
                                            variant={source === JobSource.STATIC ? 'default' : source === JobSource.DYNAMIC ? 'secondary' : 'outline'}
                                            className="w-20 justify-center"
                                        >
                                            {jobSourceLabel(source)}
                                        </Badge>
                                        <ContentArea>
                                            <SegmentedProgress segments={[{percent, tone}]} className="h-4"/>
                                        </ContentArea>
                                        <Strong className="w-16 text-right">{count}</Strong>
                                    </InlineRow>
                                )
                            })}
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </PageLayout>
    )
}
